import { useState, useEffect } from "react";
import { Sidebar, PageHeader, formatCurrency } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { 
  Plus, 
  Target, 
  Pencil, 
  Trash2, 
  TrendingDown,
  PiggyBank,
  Shield,
  Trophy,
  Sparkles
} from "lucide-react";

const GOAL_TYPES = [
  { value: "debt_payoff", label: "Debt Payoff", icon: TrendingDown, color: "text-p2p-error" },
  { value: "savings", label: "Savings Goal", icon: PiggyBank, color: "text-p2p-gold" },
  { value: "emergency_fund", label: "Emergency Fund", icon: Shield, color: "text-p2p-success" }
];

export default function GoalsPage() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [updateAmount, setUpdateAmount] = useState("");

  const currency = user?.currency || 'GBP';

  const [formData, setFormData] = useState({
    name: "",
    target_amount: "",
    current_amount: "0",
    goal_type: "savings",
    target_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`, { withCredentials: true });
      setGoals(response.data);
    } catch (error) {
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      target_amount: "",
      current_amount: "0",
      goal_type: "savings",
      target_date: "",
      notes: ""
    });
    setEditingGoal(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      target_amount: parseFloat(formData.target_amount),
      current_amount: parseFloat(formData.current_amount) || 0,
      target_date: formData.target_date || null
    };

    try {
      if (editingGoal) {
        await axios.put(`${API}/goals/${editingGoal.goal_id}`, payload, { withCredentials: true });
        toast.success('Goal updated successfully');
      } else {
        await axios.post(`${API}/goals`, payload, { withCredentials: true });
        toast.success('Goal created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchGoals();
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const handleEdit = (goal) => {
    setFormData({
      name: goal.name,
      target_amount: goal.target_amount.toString(),
      current_amount: goal.current_amount.toString(),
      goal_type: goal.goal_type,
      target_date: goal.target_date || "",
      notes: goal.notes || ""
    });
    setEditingGoal(goal);
    setDialogOpen(true);
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    
    try {
      await axios.delete(`${API}/goals/${goalId}`, { withCredentials: true });
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const handleUpdateProgress = async () => {
    if (!selectedGoal || !updateAmount) return;
    
    try {
      await axios.put(`${API}/goals/${selectedGoal.goal_id}`, {
        current_amount: parseFloat(updateAmount)
      }, { withCredentials: true });
      
      toast.success('Progress updated!');
      setUpdateDialogOpen(false);
      setSelectedGoal(null);
      setUpdateAmount("");
      fetchGoals();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const getGoalTypeInfo = (type) => {
    return GOAL_TYPES.find(t => t.value === type) || GOAL_TYPES[1];
  };

  const completedGoals = goals.filter(g => g.progress_percent >= 100);
  const activeGoals = goals.filter(g => g.progress_percent < 100);

  return (
    <Sidebar>
      <PageHeader 
        title="Goals & Milestones"
        subtitle="Track your financial targets"
        action={
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }} modal={true}>
            <DialogTrigger asChild>
              <Button className="btn-gold rounded-full" data-testid="add-goal-btn">
                <Plus className="w-4 h-4 mr-2" /> New Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2p-dark border-white/10 text-p2p-gray max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-outfit text-xl">
                  {editingGoal ? 'Edit Goal' : 'Create New Goal'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-p2p-muted">Goal Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="input-dark"
                    placeholder="e.g., Pay off credit card"
                    required
                    data-testid="goal-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-p2p-muted">Goal Type</Label>
                  <Select value={formData.goal_type} onValueChange={(val) => setFormData(prev => ({ ...prev, goal_type: val }))}>
                    <SelectTrigger className="input-dark" data-testid="goal-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-p2p-teal border-white/10">
                      {GOAL_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value} className="text-p2p-gray hover:bg-white/10">
                          <div className="flex items-center gap-2">
                            <type.icon className={`w-4 h-4 ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-p2p-muted">Target Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.target_amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, target_amount: e.target.value }))}
                      className="input-dark"
                      placeholder="0.00"
                      required
                      data-testid="goal-target-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-p2p-muted">Current Progress</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.current_amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, current_amount: e.target.value }))}
                      className="input-dark"
                      placeholder="0.00"
                      data-testid="goal-current-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-p2p-muted">Target Date (optional)</Label>
                  <Input
                    type="date"
                    value={formData.target_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_date: e.target.value }))}
                    className="input-dark"
                    data-testid="goal-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-p2p-muted">Notes (optional)</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="input-dark"
                    placeholder="Any additional notes..."
                    data-testid="goal-notes-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1 border-white/20 text-p2p-gray hover:bg-white/5"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 btn-gold" data-testid="save-goal-btn">
                    {editingGoal ? 'Update' : 'Create'} Goal
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Update Progress Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen} modal={true}>
        <DialogContent className="bg-p2p-dark border-white/10 text-p2p-gray max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Update Progress</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-p2p-muted text-sm">
              Current: {formatCurrency(selectedGoal?.current_amount || 0, currency)} of {formatCurrency(selectedGoal?.target_amount || 0, currency)}
            </p>
            <div className="space-y-2">
              <Label className="text-p2p-muted">New Amount</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={updateAmount}
                onChange={(e) => setUpdateAmount(e.target.value)}
                className="input-dark"
                placeholder={selectedGoal?.current_amount?.toString()}
                data-testid="update-progress-input"
              />
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-white/20"
                onClick={() => { setUpdateDialogOpen(false); setSelectedGoal(null); setUpdateAmount(""); }}
              >
                Cancel
              </Button>
              <Button className="flex-1 btn-gold" onClick={handleUpdateProgress} data-testid="confirm-update-btn">
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-p2p-gold"></div>
        </div>
      ) : goals.length === 0 ? (
        <div className="glass-card p-12 rounded-2xl text-center">
          <Target className="w-16 h-16 text-p2p-muted mx-auto mb-4" />
          <h3 className="font-outfit text-xl font-semibold text-p2p-gray mb-2">No goals yet</h3>
          <p className="text-p2p-muted mb-6">Set your first financial goal to start tracking progress</p>
          <Button className="btn-gold rounded-full" onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Your First Goal
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Active Goals */}
          {activeGoals.length > 0 && (
            <div>
              <h2 className="font-outfit text-lg font-semibold text-p2p-gray mb-4">Active Goals</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {activeGoals.map((goal) => {
                  const typeInfo = getGoalTypeInfo(goal.goal_type);
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <div 
                      key={goal.goal_id}
                      className="glass-card glass-card-hover p-6 rounded-2xl"
                      data-testid={`goal-${goal.goal_id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            goal.goal_type === 'debt_payoff' ? 'bg-p2p-error/20' :
                            goal.goal_type === 'emergency_fund' ? 'bg-p2p-success/20' :
                            'bg-p2p-gold/20'
                          }`}>
                            <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
                          </div>
                          <div>
                            <h3 className="font-medium text-p2p-gray">{goal.name}</h3>
                            <p className="text-xs text-p2p-muted capitalize">{typeInfo.label}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-p2p-muted hover:text-p2p-gold hover:bg-p2p-gold/10"
                            onClick={() => handleEdit(goal)}
                            data-testid={`edit-goal-${goal.goal_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-p2p-muted hover:text-p2p-error hover:bg-p2p-error/10"
                            onClick={() => handleDelete(goal.goal_id)}
                            data-testid={`delete-goal-${goal.goal_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-p2p-muted">Progress</span>
                          <span className="font-medium text-p2p-gold">{goal.progress_percent.toFixed(1)}%</span>
                        </div>
                        <Progress 
                          value={Math.min(goal.progress_percent, 100)} 
                          className="h-3 bg-white/10"
                        />
                        <div className="flex justify-between text-sm">
                          <span className="font-mono text-p2p-gray">{formatCurrency(goal.current_amount, currency)}</span>
                          <span className="font-mono text-p2p-muted">{formatCurrency(goal.target_amount, currency)}</span>
                        </div>
                      </div>

                      {goal.target_date && (
                        <p className="text-xs text-p2p-muted mt-3">
                          Target: {new Date(goal.target_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}

                      <Button 
                        variant="outline" 
                        className="w-full mt-4 border-white/20 text-p2p-gray hover:bg-white/5"
                        onClick={() => { setSelectedGoal(goal); setUpdateAmount(goal.current_amount.toString()); setUpdateDialogOpen(true); }}
                        data-testid={`update-goal-${goal.goal_id}`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" /> Update Progress
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Completed Goals */}
          {completedGoals.length > 0 && (
            <div>
              <h2 className="font-outfit text-lg font-semibold text-p2p-gray mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-p2p-gold" /> Completed Goals
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {completedGoals.map((goal) => {
                  const typeInfo = getGoalTypeInfo(goal.goal_type);
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <div 
                      key={goal.goal_id}
                      className="glass-card p-6 rounded-2xl border border-p2p-success/20 bg-p2p-success/5"
                      data-testid={`completed-goal-${goal.goal_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-p2p-success/20 flex items-center justify-center">
                            <Trophy className="w-5 h-5 text-p2p-success" />
                          </div>
                          <div>
                            <h3 className="font-medium text-p2p-gray">{goal.name}</h3>
                            <p className="text-sm text-p2p-success font-medium">Completed!</p>
                          </div>
                        </div>
                        <span className="font-mono font-bold text-p2p-success">
                          {formatCurrency(goal.target_amount, currency)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Sidebar>
  );
}
